import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, CheckCheck, X, TrendingUp, UserPlus, Dumbbell, AlertTriangle, Clock } from 'lucide-react';
import { API } from '../App';
import axios from 'axios';

const typeConfig = {
  success: { color: 'bg-emerald-500', icon: UserPlus },
  revenue: { color: 'bg-blue-500', icon: TrendingUp },
  checkin: { color: 'bg-purple-500', icon: Dumbbell },
  warning: { color: 'bg-amber-500', icon: AlertTriangle },
  reminder: { color: 'bg-sky-500', icon: Clock },
  info: { color: 'bg-slate-500', icon: Bell },
};

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnread(res.data.unread_count);
    } catch (e) { /* silent */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=20`);
      setNotifications(res.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-read`, {});
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
  };

  const markOneRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/mark-read`, { notif_id: notifId });
      setUnread(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.notif_id === notifId ? { ...n, read: true } : n));
    } catch (e) { /* silent */ }
  };

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso)) / 1000;
    if (diff < 60) return 'Agora';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
            <h3 className="text-sm font-bold text-slate-800">Notificações</h3>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="mark-all-read-btn">
                  <CheckCheck className="w-3.5 h-3.5" /> Marcar todas
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-200 rounded-lg">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Nenhuma notificação</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const cfg = typeConfig[notif.type] || typeConfig.info;
                const Icon = cfg.icon;
                return (
                  <div
                    key={notif.notif_id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${!notif.read ? 'bg-blue-50/50' : ''}`}
                    onClick={() => !notif.read && markOneRead(notif.notif_id)}
                    data-testid={`notification-item-${notif.notif_id}`}
                  >
                    <div className={`w-8 h-8 rounded-full ${cfg.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notif.read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{notif.title}</p>
                        <span className="text-[10px] text-slate-400 flex-shrink-0">{timeAgo(notif.created_at)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                    </div>
                    {!notif.read && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Push subscription helper
export const registerPushSubscription = async () => {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    // Reuse the single SW registered in index.js (handles cache + push).
    // Registering a second SW at the same scope evicts the first one,
    // breaking cache invalidation and causing white-screen on redeploy.
    const registration =
      (await navigator.serviceWorker.getRegistration('/')) ||
      (await navigator.serviceWorker.register('/sw.js'));
    await navigator.serviceWorker.ready;

    const res = await axios.get(`${API}/notifications/vapid-key`);
    const vapidKey = res.data.vapid_public_key;
    if (!vapidKey) return;

    const urlBase64ToUint8Array = (base64String) => {
      const padding = '='.repeat((4 - base64String.length % 4) % 4);
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
    };

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    });

    await axios.post(`${API}/notifications/subscribe`, {
      subscription: subscription.toJSON()
    });
  } catch (e) {
    // Push registration not available in this context
  }
};
